/**
 * L'ÉCRAN DES RELANCES — lot 156, 03/11/2026. Maquettes validées le 03/11
 * (canevas, planches « Recouvrement · 1 » et « · 2 »).
 *
 *   - la file du jour : quatre chiffres, puis trois groupes (en retard d'envoi,
 *     à envoyer aujourd'hui, gelées) ;
 *   - au clic, une fenêtre : le courrier à gauche, le mail à droite ;
 *     « Envoyer » fabrique le PDF du courrier, l'envoie avec le mail, et la
 *     file avance. Précédente / Suivante enchaînent les relances du jour.
 *
 * LE PDF EST FABRIQUÉ DANS LE NAVIGATEUR (décision du 03/11 : « joint tout
 * seul ») : la feuille A4 est photographiée (`html2canvas`) puis posée dans un
 * PDF (`jspdf`). Les deux bibliothèques ne sont chargées qu'au premier envoi.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  envoyerRelance,
  gelerRelance,
  leverGelRelance,
  lireFileRelances,
  noterHorsStafa,
  preparerCourrielRelance,
  type CourrielPrepare,
} from './apiRelances.js';
import { CourrierRelance } from './CourrierRelance.js';
import {
  AUCUN_FILTRE_RELANCES,
  basculerFiltre,
  basculerTri,
  chiffres,
  combienDeFiltresRelances,
  filtrerRelances,
  TRI_RELANCES,
  trierRelances,
  type ColonneRelances,
  type FiltresRelances,
  type TriRelances,
  dateDite,
  envoyeesCeMois,
  grouper,
  montantDit,
  nomDuPdf,
  NOMS_NIVEAUX,
  type FileRelances,
  type LigneRelance,
} from './relancesPl.js';

/** La feuille A4 en PDF, en base64 sans préfixe. */
export async function pdfDuCourrier(feuille: HTMLElement): Promise<string> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  feuille.classList.add('pour-pdf');
  try {
    const toile = await html2canvas(feuille, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    pdf.addImage(toile.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297);
    const uri = pdf.output('datauristring');
    return uri.slice(uri.indexOf(',') + 1);
  } finally {
    feuille.classList.remove('pour-pdf');
  }
}

const MOTIFS: readonly [string, string][] = [
  ['promesse', 'Promesse de paiement'],
  ['contestation', 'Facture contestée'],
  ['echeancier', 'Échéancier accepté'],
  ['litige', 'Litige déclaré'],
  ['autre', 'Autre'],
];

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export interface ProprietesVueRelances {
  readonly file: FileRelances | null;
  readonly refus: string | null;
  readonly quandOuvrir: (l: LigneRelance) => void;
  readonly quandLever: (l: LigneRelance) => void;
  /** LOT 157 : recherche, filtres et tri, comme la liste des OT. */
  readonly recherche: string;
  readonly filtres: FiltresRelances;
  readonly panneauOuvert: boolean;
  readonly tri: TriRelances;
  readonly quandRecherche: (t: string) => void;
  readonly quandFiltres: (f: FiltresRelances) => void;
  readonly quandPanneau: (ouvert: boolean) => void;
  readonly quandTri: (t: TriRelances) => void;
}

/** Les critères du panneau Filtrer : « OU » dans un critère, « ET » entre eux. */
const CRITERES: readonly {
  readonly cle: keyof FiltresRelances;
  readonly nom: string;
  readonly choix: readonly (readonly [string, string])[];
}[] = [
  {
    cle: 'niveaux',
    nom: 'Relance à envoyer',
    choix: (Object.keys(NOMS_NIVEAUX) as (keyof typeof NOMS_NIVEAUX)[]).map((n) => [n, NOMS_NIVEAUX[n]] as const),
  },
  {
    cle: 'etats',
    nom: 'État',
    choix: [
      ['retard', 'En retard d’envoi'],
      ['jour', 'À envoyer aujourd’hui'],
      ['gelee', 'Gelée'],
    ],
  },
  {
    cle: 'adresses',
    nom: 'Destinataire',
    choix: [
      ['avec', 'Avec adresse'],
      ['sans', 'Sans adresse'],
    ],
  },
];

/** Un titre de colonne qui trie au clic. */
function Tete({
  p,
  colonne,
  texte,
  droite,
}: {
  readonly p: ProprietesVueRelances;
  readonly colonne: ColonneRelances;
  readonly texte: string;
  readonly droite?: boolean;
}) {
  const active = p.tri.colonne === colonne;
  return (
    <th
      scope="col"
      className={[droite === true ? 'n' : '', active ? 'triee' : ''].join(' ').trim() || undefined}
      aria-sort={active ? (p.tri.sens === 'croissant' ? 'ascending' : 'descending') : 'none'}
    >
      <button className="tete-tri" type="button" onClick={() => p.quandTri(basculerTri(p.tri, colonne))}>
        {texte}
        {active && (
          <span className="sens-tri" aria-hidden="true">
            {p.tri.sens === 'croissant' ? '▲' : '▼'}
          </span>
        )}
      </button>
    </th>
  );
}

function Tableau({
  titre,
  pastille,
  lignes,
  p,
  gelees,
}: {
  readonly titre: string;
  readonly pastille: string;
  readonly lignes: readonly LigneRelance[];
  readonly p: ProprietesVueRelances;
  readonly gelees?: boolean;
}) {
  if (lignes.length === 0) return null;
  return (
    <>
      <div className="rp-groupe">
        {titre} <span className={`rp-pastille ${pastille}`}>{lignes.length}</span>
      </div>
      <div className="rp-cadre">
        <table className="rp-table">
          <thead>
            <tr>
              <Tete p={p} colonne="client" texte="Client" />
              <Tete p={p} colonne="numero" texte="Facture" />
              <Tete p={p} colonne="echeance" texte="Échéance" />
              <Tete p={p} colonne="retard" texte="Retard" droite />
              <Tete p={p} colonne="reste" texte="Reste dû TTC" droite />
              <th>Dernière relance</th>
              {gelees === true ? <th>Gel</th> : <Tete p={p} colonne="niveau" texte="À envoyer" />}
              <th>Destinataire</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const a = l.a_envoyer;
              const adresse = l.client?.courriel_facturation ?? null;
              return (
                <tr key={l.pennylane_facture_id}>
                  <td className="rp-client">{l.client?.nom ?? l.libelle ?? '—'}</td>
                  <td>{l.numero ?? '—'}</td>
                  <td>{dateDite(l.echeance)}</td>
                  <td className="n">{l.retard_jours > 0 ? `${String(l.retard_jours)} j` : 'à échoir'}</td>
                  <td className="n">{montantDit(Number(l.reste_du))} €</td>
                  <td>
                    {l.derniere === null ? (
                      <span className="rp-vide">—</span>
                    ) : (
                      <>
                        {NOMS_NIVEAUX[l.derniere.niveau]}
                        <span className="rp-sous">{dateDite(l.derniere.envoyee_le)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    {gelees === true && l.gel !== null ? (
                      <>
                        {MOTIFS.find(([c]) => c === l.gel?.motif)?.[1] ?? l.gel.motif}
                        <span className="rp-sous">jusqu’au {dateDite(l.gel.reprise_le)}</span>
                      </>
                    ) : a === null ? (
                      '—'
                    ) : (
                      <>
                        <span className={`rp-niveau rp-${a.niveau}`}>{a.nom}</span>
                        {a.groupe === 'retard' && <span className="rp-sous">prévue le {dateDite(a.prevu_le)}</span>}
                      </>
                    )}
                  </td>
                  <td>{adresse ?? <span className="rp-refus">Aucune adresse</span>}</td>
                  <td className="rp-actions">
                    {gelees === true ? (
                      <button className="bouton bouton-discret" type="button" onClick={() => p.quandLever(l)}>
                        Lever le gel
                      </button>
                    ) : (
                      <button
                        className={adresse === null ? 'bouton bouton-discret' : 'bouton'}
                        type="button"
                        onClick={() => p.quandOuvrir(l)}
                      >
                        {adresse === null ? 'Ajouter l’adresse' : 'Voir le courrier'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function VueRelancesPl(p: ProprietesVueRelances) {
  if (p.file === null) {
    return p.refus === null ? <p className="attente">Chargement des relances…</p> : <p className="bandeau-refus">{p.refus}</p>;
  }
  // LES CHIFFRES PORTENT SUR TOUTE LA FILE ; les tableaux, sur ce qui reste
  // après la recherche et les filtres, trié par la colonne choisie.
  const vues = trierRelances(filtrerRelances(p.file.lignes, p.filtres, p.recherche), p.tri);
  const g = grouper(vues);
  const c = chiffres(p.file.lignes, envoyeesCeMois(p.file.lignes, p.file.jour));
  const nFiltres = combienDeFiltresRelances(p.filtres);
  return (
    <div className="rp-ecran">
      {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}
      <div className="barre-outils">
        <input
          className="recherche"
          type="search"
          placeholder="Client, n° de facture, adresse…"
          aria-label="Rechercher une relance"
          value={p.recherche}
          onChange={(e) => p.quandRecherche(e.target.value)}
        />
        <button
          className="bouton bouton-discret"
          type="button"
          aria-expanded={p.panneauOuvert}
          onClick={() => p.quandPanneau(!p.panneauOuvert)}
        >
          Filtrer
          {nFiltres > 0 && <span className="compte-filtres"> · {nFiltres}</span>}
        </button>
        {(nFiltres > 0 || p.recherche !== '') && (
          <span className="rp-compte">
            {vues.length} sur {p.file.lignes.length}
          </span>
        )}
      </div>
      {p.panneauOuvert && (
        <div className="panneau-filtres">
          <div className="grille-criteres">
            {CRITERES.map((critere) => (
              <fieldset className="critere" key={critere.cle}>
                <legend className="critere-nom">{critere.nom}</legend>
                {critere.choix.map(([valeur, texte], rang) => (
                  <Fragment key={valeur}>
                    {rang === 1 && <p className="ou-filtre">OU</p>}
                    <label>
                      <input
                        type="checkbox"
                        checked={(p.filtres[critere.cle] as readonly string[]).includes(valeur)}
                        onChange={() =>
                          p.quandFiltres(
                            basculerFiltre(p.filtres, critere.cle, valeur as never),
                          )
                        }
                      />
                      {texte}
                    </label>
                  </Fragment>
                ))}
              </fieldset>
            ))}
          </div>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={nFiltres === 0}
            onClick={() => p.quandFiltres(AUCUN_FILTRE_RELANCES)}
          >
            Tout décocher
          </button>
        </div>
      )}
      <div className="rp-chiffres">
        <div className="rp-chiffre">
          <span>À envoyer aujourd’hui</span>
          <b>{c.a_envoyer}</b>
        </div>
        <div className="rp-chiffre">
          <span>dont en retard d’envoi</span>
          <b className={c.en_retard > 0 ? 'rp-rouge' : ''}>{c.en_retard}</b>
        </div>
        <div className="rp-chiffre">
          <span>Montant relancé</span>
          <b>{montantDit(c.montant_centimes / 100)} €</b>
        </div>
        <div className="rp-chiffre">
          <span>Envoyées ce mois</span>
          <b>{c.envoyees_ce_mois}</b>
        </div>
      </div>
      {g.retard.length + g.jour.length + g.gelees.length === 0 && (
        <p className="rp-vide-file">Aucune relance à envoyer aujourd’hui.</p>
      )}
      <Tableau titre="En retard d’envoi" pastille="rp-p-rouge" lignes={g.retard} p={p} />
      <Tableau titre="À envoyer aujourd’hui" pastille="rp-p-bleu" lignes={g.jour} p={p} />
      <Tableau titre="Gelées" pastille="rp-p-gris" lignes={g.gelees} p={p} gelees />
    </div>
  );
}

// ---------------------------------------------------------------------------
// La fenêtre d'envoi
// ---------------------------------------------------------------------------

export interface ProprietesFenetre {
  readonly ligne: LigneRelance;
  readonly jour: string;
  readonly rang: number;
  readonly total: number;
  readonly courriel: CourrielPrepare | null;
  readonly destinataires: string;
  readonly copie: string;
  readonly joindreFacture: boolean;
  readonly occupe: boolean;
  readonly message: string | null;
  readonly gel: boolean;
  readonly quandDestinataires: (v: string) => void;
  readonly quandCopie: (v: string) => void;
  readonly quandJoindre: (v: boolean) => void;
  readonly quandEnvoyer: (feuille: HTMLElement | null) => void;
  readonly quandTelecharger: (feuille: HTMLElement | null) => void;
  readonly quandHorsStafa: () => void;
  readonly quandGel: (ouvert: boolean) => void;
  readonly quandGeler: (motif: string, reprise: string, note: string) => void;
  readonly quandPrecedente: () => void;
  readonly quandSuivante: () => void;
  readonly quandFermer: () => void;
}

export function FenetreRelance(p: ProprietesFenetre) {
  const feuille = useRef<HTMLDivElement>(null);
  const [motif, setMotif] = useState('promesse');
  const [reprise, setReprise] = useState('');
  const [note, setNote] = useState('');
  const a = p.ligne.a_envoyer;
  if (a === null) return null;
  return (
    <div className="rp-voile" role="presentation">
      <div className="rp-fenetre" role="dialog" aria-label={`${a.nom} — ${p.ligne.numero ?? ''}`}>
        <div className="rp-f-tete">
          <span className={`rp-niveau rp-${a.niveau}`}>{a.nom}</span>
          <b className="rp-f-titre">
            {p.ligne.client?.nom ?? p.ligne.libelle ?? 'Client'} · {p.ligne.numero ?? '—'}
          </b>
          <span className="rp-f-resume">
            reste dû {montantDit(Number(p.ligne.reste_du))} € ·{' '}
            {p.ligne.retard_jours > 0 ? `échue depuis ${String(p.ligne.retard_jours)} j` : 'à échoir'}
            {a.groupe === 'retard' ? ` · prévue le ${dateDite(a.prevu_le)}` : ''}
          </span>
          <span className="rp-f-nav">
            <span>
              {p.rang + 1} sur {p.total}
            </span>
            <button className="bouton bouton-discret" type="button" disabled={p.rang === 0 || p.occupe} onClick={p.quandPrecedente}>
              Précédente
            </button>
            <button className="bouton bouton-discret" type="button" disabled={p.rang >= p.total - 1 || p.occupe} onClick={p.quandSuivante}>
              Suivante
            </button>
            <button className="rp-fermer" type="button" aria-label="Fermer" onClick={p.quandFermer}>
              ×
            </button>
          </span>
        </div>
        <div className="rp-f-corps">
          <div className="rp-papier">
            {/* LA COPIE PHOTOGRAPHIÉE POUR LE PDF, à taille réelle et hors de
                l'écran : la version visible est réduite par une transformation,
                que la photographie reprendrait. */}
            <div className="rp-hors-ecran" aria-hidden="true">
              <CourrierRelance
                ref={feuille}
                ligne={p.ligne}
                niveau={a.niveau}
                jour={p.jour}
                interets={a.interets}
                indemnite={a.indemnite}
                montantDu={a.montant_du}
              />
            </div>
            <div className="rp-reduit">
              <CourrierRelance
                ligne={p.ligne}
                niveau={a.niveau}
                jour={p.jour}
                interets={a.interets}
                indemnite={a.indemnite}
                montantDu={a.montant_du}
              />
            </div>
          </div>
          <div className="rp-mail">
            <label className="rp-l">
              <span>À</span>
              <input className="boite" value={p.destinataires} onChange={(e) => p.quandDestinataires(e.target.value)} />
            </label>
            <label className="rp-l">
              <span>Copie</span>
              <input className="boite" value={p.copie} onChange={(e) => p.quandCopie(e.target.value)} />
            </label>
            <div className="rp-l">
              <span>Objet</span>
              <span className="rp-objet">{p.courriel?.sujet ?? '…'}</span>
            </div>
            <div className="rp-texte">{p.courriel?.texte ?? 'Préparation du mail…'}</div>
            <div className="rp-pj">
              <span className="rp-pj-ic">PDF</span>
              <span>{nomDuPdf(a.niveau, p.ligne.numero, p.ligne.client?.nom ?? null)}</span>
            </div>
            <label className="rp-case">
              <input type="checkbox" checked={p.joindreFacture} onChange={(e) => p.quandJoindre(e.target.checked)} />
              Joindre la facture {p.ligne.numero ?? ''} (Pennylane)
            </label>
            {p.message !== null && <p className="rp-message">{p.message}</p>}
            {p.gel && (
              <div className="rp-gel">
                <label className="rp-l">
                  <span>Motif</span>
                  <select className="boite" value={motif} onChange={(e) => setMotif(e.target.value)}>
                    {MOTIFS.map(([c, n]) => (
                      <option key={c} value={c}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rp-l">
                  <span>Reprise</span>
                  <input className="boite" type="date" value={reprise} onChange={(e) => setReprise(e.target.value)} />
                </label>
                <label className="rp-l">
                  <span>Note</span>
                  <input className="boite" value={note} onChange={(e) => setNote(e.target.value)} />
                </label>
                <div className="rp-gel-actions">
                  <button className="bouton bouton-discret" type="button" onClick={() => p.quandGel(false)}>
                    Annuler
                  </button>
                  <button
                    className="bouton"
                    type="button"
                    disabled={reprise === '' || p.occupe}
                    onClick={() => p.quandGeler(motif, reprise, note)}
                  >
                    Geler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="rp-f-pied">
          <button className="bouton bouton-discret" type="button" disabled={p.occupe} onClick={() => p.quandGel(true)}>
            Geler cette facture…
          </button>
          <button className="bouton bouton-discret" type="button" disabled={p.occupe} onClick={p.quandHorsStafa}>
            Déjà envoyée hors Stafa
          </button>
          <span className="rp-f-droite">
            <button className="bouton bouton-discret" type="button" disabled={p.occupe} onClick={() => p.quandTelecharger(feuille.current)}>
              Télécharger le PDF
            </button>
            <button
              className="bouton"
              type="button"
              disabled={p.occupe || p.courriel === null || p.destinataires.trim() === ''}
              onClick={() => p.quandEnvoyer(feuille.current)}
            >
              {p.occupe ? 'Envoi…' : 'Envoyer'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

/** Une liste d'adresses saisie « a@x.fr, b@y.fr » → ['a@x.fr', 'b@y.fr']. */
export function adressesDe(saisie: string): readonly string[] {
  return saisie
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter((a) => a !== '');
}

export function EcranRelancesPl({
  jeton,
  quandAccesPerdu,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}) {
  const [file, setFile] = useState<FileRelances | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [courriel, setCourriel] = useState<CourrielPrepare | null>(null);
  const [destinataires, setDestinataires] = useState('');
  const [copie, setCopie] = useState('');
  const [joindre, setJoindre] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [gel, setGel] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [filtres, setFiltres] = useState<FiltresRelances>(AUCUN_FILTRE_RELANCES);
  const [panneau, setPanneau] = useState(false);
  const [tri, setTri] = useState<TriRelances>(TRI_RELANCES);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const relire = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const r = await lireFileRelances(j);
    if (r.etat === 'ok') {
      setFile(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? 'Les relances n’ont pas pu être lues.');
    }
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void relire();
  }, [relire]);

  // LA FILE À PARCOURIR dans la fenêtre : ce qui est à envoyer, retard d'abord.
  const aParcourir = useMemo(() => {
    if (file === null) return [];
    // LA FENÊTRE PARCOURT CE QUE L'ON VOIT : recherche, filtres et tri compris.
    const g = grouper(trierRelances(filtrerRelances(file.lignes, filtres, recherche), tri));
    return [...g.retard, ...g.jour];
  }, [file, filtres, recherche, tri]);
  const rang = aParcourir.findIndex((l) => l.pennylane_facture_id === ouverte);
  const ligne = rang >= 0 ? (aParcourir[rang] ?? null) : null;

  const ouvrir = useCallback(
    async (l: LigneRelance) => {
      setOuverte(l.pennylane_facture_id);
      setCourriel(null);
      setMessage(null);
      setGel(false);
      setJoindre(false);
      setDestinataires(l.client?.courriel_facturation ?? '');
      setCopie('');
      const j = await avecJeton();
      if (j === null) return;
      const r = await preparerCourrielRelance(j, l.pennylane_facture_id);
      if (r.etat === 'ok') {
        setCourriel(r.valeur);
        setDestinataires(r.valeur.destinataires.join(', '));
        setCopie(r.valeur.copie.join(', '));
      } else {
        setMessage(r.message ?? 'Le mail n’a pas pu être préparé.');
      }
    },
    [avecJeton],
  );

  /** Après un geste réussi : on relit la file et on passe à la suivante. */
  const apres = useCallback(
    async (texte: string) => {
      const suivante = aParcourir[rang + 1] ?? null;
      await relire();
      if (suivante === null) {
        setOuverte(null);
        setRefus(null);
      } else {
        await ouvrir(suivante);
      }
      setMessage(texte);
    },
    [aParcourir, rang, relire, ouvrir],
  );

  return (
    <>
      <VueRelancesPl
        file={file}
        refus={refus}
        recherche={recherche}
        filtres={filtres}
        panneauOuvert={panneau}
        tri={tri}
        quandRecherche={setRecherche}
        quandFiltres={setFiltres}
        quandPanneau={setPanneau}
        quandTri={setTri}
        quandOuvrir={(l) => void ouvrir(l)}
        quandLever={(l) => {
          void (async () => {
            const j = await avecJeton();
            if (j === null) return;
            const r = await leverGelRelance(j, l.pennylane_facture_id);
            if (r.etat !== 'ok') setRefus(r.message ?? 'Le gel n’a pas pu être levé.');
            await relire();
          })();
        }}
      />
      {ligne !== null && file !== null && (
        <FenetreRelance
          ligne={ligne}
          jour={file.jour}
          rang={rang}
          total={aParcourir.length}
          courriel={courriel}
          destinataires={destinataires}
          copie={copie}
          joindreFacture={joindre}
          occupe={occupe}
          message={message}
          gel={gel}
          quandDestinataires={setDestinataires}
          quandCopie={setCopie}
          quandJoindre={setJoindre}
          quandGel={setGel}
          quandFermer={() => setOuverte(null)}
          quandPrecedente={() => {
            const p = aParcourir[rang - 1];
            if (p !== undefined) void ouvrir(p);
          }}
          quandSuivante={() => {
            const s = aParcourir[rang + 1];
            if (s !== undefined) void ouvrir(s);
          }}
          quandTelecharger={(feuille) => {
            if (feuille === null || ligne.a_envoyer === null) return;
            const niveau = ligne.a_envoyer.niveau;
            void (async () => {
              setOccupe(true);
              try {
                const b64 = await pdfDuCourrier(feuille);
                const lien = document.createElement('a');
                lien.href = `data:application/pdf;base64,${b64}`;
                lien.download = nomDuPdf(niveau, ligne.numero, ligne.client?.nom ?? null);
                lien.click();
              } finally {
                setOccupe(false);
              }
            })();
          }}
          quandEnvoyer={(feuille) => {
            if (feuille === null || courriel === null) return;
            void (async () => {
              setOccupe(true);
              setMessage(null);
              try {
                const j = await avecJeton();
                if (j === null) return;
                const b64 = await pdfDuCourrier(feuille);
                const r = await envoyerRelance(j, ligne.pennylane_facture_id, {
                  niveau: courriel.niveau,
                  destinataires: adressesDe(destinataires),
                  copie: adressesDe(copie),
                  piece_nom: nomDuPdf(courriel.niveau, ligne.numero, ligne.client?.nom ?? null),
                  piece_base64: b64,
                  joindre_facture: joindre,
                });
                if (r.etat !== 'ok') {
                  setMessage(r.message ?? 'Le mail n’est pas parti.');
                } else if (!r.valeur.parti) {
                  setMessage(`Le mail n’est pas parti : ${r.valeur.message}`);
                } else {
                  await apres(`Envoyée : ${r.valeur.sujet}`);
                }
              } finally {
                setOccupe(false);
              }
            })();
          }}
          quandHorsStafa={() => {
            if (ligne.a_envoyer === null) return;
            const niveau = ligne.a_envoyer.niveau;
            void (async () => {
              setOccupe(true);
              try {
                const j = await avecJeton();
                if (j === null) return;
                const r = await noterHorsStafa(j, ligne.pennylane_facture_id, niveau, file.jour);
                if (r.etat !== 'ok') setMessage(r.message ?? 'La relance n’a pas pu être notée.');
                else await apres(`${NOMS_NIVEAUX[niveau]} notée comme envoyée hors Stafa.`);
              } finally {
                setOccupe(false);
              }
            })();
          }}
          quandGeler={(motif, reprise, note) => {
            void (async () => {
              setOccupe(true);
              try {
                const j = await avecJeton();
                if (j === null) return;
                const r = await gelerRelance(j, ligne.pennylane_facture_id, {
                  motif,
                  reprise_le: reprise,
                  note: note.trim() === '' ? null : note.trim(),
                });
                if (r.etat !== 'ok') setMessage(r.message ?? 'Le gel n’a pas pu être posé.');
                else await apres('Facture gelée.');
              } finally {
                setOccupe(false);
              }
            })();
          }}
        />
      )}
    </>
  );
}
